import {
  Box,
  VStack,
  HStack,
  Text,
  Avatar,
  Badge,
  Icon,
  SimpleGrid,
  Divider,
  Button,
} from '@chakra-ui/react'
import { LuArrowLeft, LuSparkles } from 'react-icons/lu'
import { useNavigate } from 'react-router-dom'
import ProfileForm from '../../components/Forms/profileForm'
import SubscriptionInfo from '../../components/Profile/SubscriptionInfo'
import CollaboratorsPanel from '../../components/Profile/CollaboratorsPanel'
import MyTeamPanel from '../../components/Profile/MyTeamPanel'
import AccessLogPanel from '../../components/Profile/AccessLogPanel'
import PilotAccessPanel from '../../components/Profile/PilotAccessPanel'
import ScanInboxPanel from '../../components/Profile/ScanInboxPanel'
import LegalDocsPanel from '../../components/Profile/LegalDocsPanel'
import { useTranslation } from 'react-i18next'
import { useMyProfileWithModules } from '../../api/api_hooks/user/myProfile'
import { PlanType } from '../../types/subscription'
import { CHROME_HEADER_GRADIENT, CHROME_AURORA_VIOLET, CHROME_AURORA_CYAN } from '../../theme/chrome'
import { useAuthedImage } from '../../hooks/useAuthedImage'

const PLAN_LABELS: Partial<Record<PlanType, string>> = {
  [PlanType.PRO]: 'Essentiel',
  [PlanType.PREMIUM]: 'Premium',
  [PlanType.ENTERPRISE]: 'Institution',
  [PlanType.ENTERPRISE_PENDING]: 'profile.subscription.enterprisePending',
}

const PLAN_COLORS: Partial<Record<PlanType, string>> = {
  [PlanType.PRO]: 'teal',
  [PlanType.PREMIUM]: 'orange',
  [PlanType.ENTERPRISE]: 'purple',
  [PlanType.ENTERPRISE_PENDING]: 'yellow',
}

export default function MyInfo() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: user } = useMyProfileWithModules()
  const avatarSrc = useAuthedImage(user?.avatarFilePath)

  const plan = user?.plan as PlanType | undefined
  const planLabel = plan ? t(PLAN_LABELS[plan] ?? '') : null
  const planColor = plan ? PLAN_COLORS[plan] : 'gray'
  const isPremium = plan === PlanType.PREMIUM

  return (
    <Box bg="gray.50" minH="100vh" overflowY="auto">

      {/* Top bar */}
      <Box bg="white" borderBottom="1px solid" borderColor="gray.100" px={8} py={4}>
        <Button
          leftIcon={<Icon as={LuArrowLeft} />}
          variant="ghost"
          size="sm"
          colorScheme="gray"
          onClick={() => navigate('/home')}
        >
          Retour
        </Button>
      </Box>

      <Box maxW="1100px" mx="auto" px={6} py={10}>
        <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={6} alignItems="start">

          {/* Left column — identity + plan */}
          <VStack spacing={5} align="stretch">

            {/* Avatar card */}
            <Box bg="white" borderRadius="2xl" border="1px solid" borderColor="gray.100" boxShadow="sm" overflow="hidden">
              {/* Gradient band */}
              <Box h="80px" position="relative" overflow="hidden" bgGradient={CHROME_HEADER_GRADIENT}>
                <Box position="absolute" top="-40px" right="-20px" w="160px" h="160px" borderRadius="full"
                  bg={CHROME_AURORA_VIOLET} pointerEvents="none" />
                <Box position="absolute" bottom="-50px" left="20%" w="180px" h="180px" borderRadius="full"
                  bg={CHROME_AURORA_CYAN} pointerEvents="none" />
              </Box>

              <Box px={5} pb={5}>
                {/* Avatar overlapping the gradient */}
                <Box mt="-36px" mb={3}>
                  <Avatar
                    size="xl"
                    name={user?.contactName || ''}
                    src={avatarSrc ?? ''}
                    border="3px solid white"
                    boxShadow="md"
                  />
                </Box>

                <Text fontWeight="700" fontSize="lg" color="gray.800" lineHeight="1.2">
                  {t('shared.common.dr')} {user?.contactName}
                </Text>
                {user?.companyName && (
                  <Text fontSize="sm" color="gray.400" mt={0.5}>{user.companyName}</Text>
                )}
                {user?.email && (
                  <Text fontSize="xs" color="gray.400" mt={0.5}>{user.email}</Text>
                )}
                {user?.invitedBy && (
                  <Text fontSize="xs" color="gray.400" mt={0.5}>
                    Invité(e) par {user.invitedBy.contactName || user.invitedBy.email}
                  </Text>
                )}

                {planLabel && plan !== PlanType.FREE && (
                  <HStack spacing={1.5} mt={3}>
                    {isPremium && <Icon as={LuSparkles} boxSize={3} color="orange.400" />}
                    <Badge
                      colorScheme={planColor}
                      borderRadius="full"
                      px={2.5}
                      py={0.5}
                      fontSize="xs"
                      fontWeight="semibold"
                    >
                      {planLabel}
                    </Badge>
                  </HStack>
                )}
              </Box>
            </Box>

            {/* Pilot access countdown — only shown when accessExpiresAt is a date
                (invited pilots on the 90-day window). Users on unlimited access
                (accessExpiresAt = null) don't see this panel. */}
            <PilotAccessPanel accessExpiresAt={user?.accessExpiresAt} userRole={user?.role} />

            {/* Subscription + Credits */}
            <SubscriptionInfo
              plan={user?.plan}
              modules={user?.modules?.filter((m) => m.isEnabled).map((m) => m.moduleType)}
              canChangePlan={user?.canChangePlan ?? true}
            />

            {/* Who this chef has invited — hidden entirely when empty. */}
            <CollaboratorsPanel />

            {/* Who invited ME, and my teammates — hidden entirely for a chef. */}
            <MyTeamPanel />
          </VStack>

          {/* Right column — profile form */}
          <Box
            gridColumn={{ lg: 'span 2' }}
            bg="white"
            borderRadius="2xl"
            border="1px solid"
            borderColor="gray.100"
            boxShadow="sm"
            p={8}
          >
            <Text fontWeight="700" fontSize="xl" color="gray.800" mb={1}>
              {t('profile.personalInfo')}
            </Text>
            <Text fontSize="sm" color="gray.400" mb={6}>
              Cliquez sur un champ pour le modifier — chaque changement est enregistré automatiquement.
            </Text>
            <Divider mb={6} />
            <ProfileForm />
          </Box>

        </SimpleGrid>

        {/* Scan-by-email — forward photocopier scans straight to Careflai.
            Mounted full-width above the access log since users will read it
            more often than the RGPD log. */}
        <Box mt={6}>
          <ScanInboxPanel />
        </Box>

        {/* RGPD transparency log — admin shadow accesses on the user's cases.
            Mounted full-width below the two-column profile grid so it has
            enough room for the action table. */}
        <Box mt={6}>
          <AccessLogPanel />
        </Box>

        {/* Permanent access to the legal documents for logged-in users. */}
        <Box mt={6}>
          <LegalDocsPanel />
        </Box>
      </Box>
    </Box>
  )
}
